import { Building2, ChevronDown, MessageSquare, Phone, Menu, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useOutlet } from 'react-router-dom';

import { useBrand } from '@/brand/brand-provider';
import { CookieNotice } from '@/components/marketing/cookie-notice';
import { AnimatePresence, MotionProvider, motion } from '@/lib/motion';
import { ThemeToggle } from '@/components/theme-toggle';
import { Button } from '@/components/ui/button';

export function MarketingShell(): JSX.Element {
  const brand = useBrand();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  // Snapshot of the matched route element — lets AnimatePresence keep the old
  // page on screen while it fades out (a bare <Outlet /> would swap contents
  // mid-exit).
  const outlet = useOutlet();

  // SPA navigations keep the previous scroll position by default; a new page
  // should start at the top (skip in-page hash jumps).
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const handler = (): void => setScrolled(window.scrollY > 8);
    handler();
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // Close the mobile menu on route change.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile menu is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <MotionProvider>
    <div className="flex min-h-screen flex-col">
      <header
        className={`sticky top-0 z-40 transition-all ${
          scrolled
            ? 'border-b border-border bg-background/80 backdrop-blur-md'
            : 'border-b border-transparent bg-transparent'
        }`}
      >
        <div className="container flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-semibold">
            {brand.logoUrl ? (
              <img src={brand.logoUrl} alt={brand.displayName} className="h-8 w-8" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[var(--brand-accent)] to-fuchsia-500 text-sm font-bold text-white">
                {brand.displayName.charAt(0)}
              </div>
            )}
            <span>{brand.displayName}</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-6 md:flex">
            <ProductsDropdown />
            <IndustriesDropdown />
            <NavItem to="/blog">Blog</NavItem>
            <NavItem to="/pricing">Pricing</NavItem>
            <NavItem to="/contact">Contact</NavItem>
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex md:items-center md:gap-2">
              <ThemeToggle />
              <Button asChild variant="ghost" size="sm">
                <Link to="/login">Sign in</Link>
              </Button>
              <Button asChild size="sm">
                <Link to="/signup">Get started</Link>
              </Button>
            </div>
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md md:hidden"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
        {mobileOpen ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-border bg-background md:hidden"
          >
            <nav className="container flex flex-col gap-1 py-4">
              <p className="px-3 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Products
              </p>
              {PRODUCT_LINKS.map((p) => (
                <MobileLink key={p.to} to={p.to}>
                  <span className="flex items-center gap-2">
                    <p.icon className="h-4 w-4" />
                    {p.label}
                  </span>
                </MobileLink>
              ))}
              <div className="my-1 border-t border-border" />
              <p className="px-3 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Industries
              </p>
              {INDUSTRY_LINKS.map((l) => (
                <MobileLink key={l.to} to={l.to}>{l.label}</MobileLink>
              ))}
              <div className="my-1 border-t border-border" />
              <MobileLink to="/blog">Blog</MobileLink>
              <MobileLink to="/pricing">Pricing</MobileLink>
              <MobileLink to="/contact">Contact</MobileLink>
              <MobileLink to="/login">Sign in</MobileLink>
              <Button asChild className="mt-2">
                <Link to="/signup">Get started</Link>
              </Button>
              <div className="mt-3 flex justify-end">
                <ThemeToggle />
              </div>
            </nav>
          </motion.div>
        ) : null}
        </AnimatePresence>
      </header>

      <main className="flex-1">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            {outlet}
          </motion.div>
        </AnimatePresence>
      </main>

      <footer className="border-t border-border bg-muted/20">
        <div className="container py-12">
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-6">
            <div className="col-span-2 sm:col-span-3 lg:col-span-2">
              <div className="flex items-center gap-2 font-semibold">
                {brand.logoUrl ? (
                  <img src={brand.logoUrl} alt={brand.displayName} className="h-7 w-7" />
                ) : (
                  <div className="flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-[var(--brand-accent)] to-fuchsia-500 text-xs font-bold text-white">
                    {brand.displayName.charAt(0)}
                  </div>
                )}
                <span>{brand.displayName}</span>
              </div>
              <p className="mt-3 max-w-xs text-sm text-muted-foreground">
                Never miss another call. AI receptionists for small businesses
                that take their phone seriously.
              </p>
              <p className="mt-3 text-sm text-muted-foreground">
                <a href="tel:+13158093245" className="hover:underline">
                  +1 (315) 809-3245
                </a>
                <br />
                Chicago, IL, USA
              </p>
            </div>
            <FooterCol
              title="Product"
              links={[
                { label: 'AI Voice Agent', href: '/voice' },
                { label: 'AI Chat Widget', href: '/chat' },
                { label: 'Pricing', href: '/pricing' },
                { label: 'Contact', href: '/contact' },
              ]}
            />
            <FooterCol
              title="Industries"
              links={[
                { label: 'Dental practices', href: '/industries/dental' },
                { label: 'Medical clinics', href: '/industries/medical' },
                { label: 'Law firms', href: '/industries/legal' },
                { label: 'Real estate', href: '/industries/real-estate' },
                { label: 'Trades', href: '/industries/trades' },
                { label: 'Salons & spas', href: '/industries/salons' },
                { label: 'Hospitality', href: '/industries/hospitality' },
              ]}
            />
            <FooterCol
              title="Resources"
              links={[
                { label: 'Blog', href: '/blog' },
                { label: 'vs AnswerConnect', href: '/compare/vs-answerconnect' },
                { label: 'vs Ruby', href: '/compare/vs-ruby' },
                { label: 'Sign in', href: '/login' },
                { label: 'Get started', href: '/signup' },
              ]}
            />
            <FooterCol
              title="Legal"
              links={[
                { label: 'Terms', href: '/terms' },
                { label: 'Privacy', href: '/privacy' },
                { label: 'Acceptable Use', href: '/acceptable-use' },
                { label: 'DPA', href: '/dpa' },
                { label: 'Cookies', href: '/cookies' },
              ]}
            />
          </div>
          <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-border pt-6 text-sm text-muted-foreground sm:flex-row">
            <span>
              &copy; {new Date().getFullYear()} {brand.legalName}. All rights
              reserved.
            </span>
            <a href={`mailto:${brand.supportEmail}`} className="hover:underline">
              {brand.supportEmail}
            </a>
          </div>
        </div>
      </footer>

      <CookieNotice />
    </div>
    </MotionProvider>
  );
}

const INDUSTRY_LINKS = [
  { to: '/industries/dental', label: 'Dental practices' },
  { to: '/industries/medical', label: 'Medical clinics' },
  { to: '/industries/legal', label: 'Law firms' },
  { to: '/industries/real-estate', label: 'Real estate' },
  { to: '/industries/trades', label: 'Trades & contractors' },
  { to: '/industries/salons', label: 'Salons & spas' },
  { to: '/industries/hospitality', label: 'Hospitality' },
  { to: '/industries/professional-services', label: 'Professional services' },
] as const;

const PRODUCT_LINKS = [
  {
    to: '/voice',
    label: 'AI Voice Agent',
    blurb: 'Answers every phone call, 24/7.',
    icon: Phone,
  },
  {
    to: '/chat',
    label: 'AI Chat Widget',
    blurb: 'Answers every website visitor, instantly.',
    icon: MessageSquare,
  },
] as const;

/**
 * Accessible hover/click dropdown shared by Products and Industries.
 * Keyboard support per the WAI-ARIA menu-button pattern: ArrowDown/ArrowUp
 * move through items (opening if closed), Home/End jump, Escape closes and
 * returns focus to the trigger, outside click closes.
 */
function NavDropdown({
  label,
  panelWidth,
  children,
}: {
  label: string;
  panelWidth: string;
  children: React.ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const focusItem = (target: 'first' | 'last' | 1 | -1): void => {
    const items = Array.from(
      ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    if (items.length === 0) return;
    const idx = items.indexOf(document.activeElement as HTMLElement);
    const next =
      target === 'first' || (typeof target === 'number' && idx === -1)
        ? items[0]
        : target === 'last'
          ? items[items.length - 1]
          : items[(idx + target + items.length) % items.length];
    next?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) setOpen(true);
        requestAnimationFrame(() => focusItem(open ? 1 : 'first'));
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) setOpen(true);
        requestAnimationFrame(() => focusItem(open ? -1 : 'last'));
        break;
      case 'Home':
        if (open) {
          e.preventDefault();
          focusItem('first');
        }
        break;
      case 'End':
        if (open) {
          e.preventDefault();
          focusItem('last');
        }
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          setOpen(false);
          triggerRef.current?.focus();
        }
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
    }
  };

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onKeyDown={onKeyDown}
    >
      <button
        ref={triggerRef}
        type="button"
        className="flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <ChevronDown
          className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <AnimatePresence>
        {open ? (
          <div role="menu" className={`absolute left-0 top-full ${panelWidth} pt-2`}>
            <motion.div
              initial={{ opacity: 0, y: 4, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 4, scale: 0.98 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="rounded-xl border border-border bg-background p-2 shadow-lg"
              onClick={() => setOpen(false)}
            >
              {children}
            </motion.div>
          </div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function ProductsDropdown(): JSX.Element {
  return (
    <NavDropdown label="Products" panelWidth="w-72">
      {PRODUCT_LINKS.map((p) => (
        <Link
          key={p.to}
          to={p.to}
          role="menuitem"
          className="flex items-start gap-3 rounded-lg p-3 hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none"
        >
          <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-muted">
            <p.icon className="h-4 w-4" />
          </span>
          <span>
            <span className="block text-sm font-medium text-foreground">
              {p.label}
            </span>
            <span className="block text-xs text-muted-foreground">
              {p.blurb}
            </span>
          </span>
        </Link>
      ))}
    </NavDropdown>
  );
}

function IndustriesDropdown(): JSX.Element {
  return (
    <NavDropdown label="Industries" panelWidth="w-56">
      {INDUSTRY_LINKS.map((l) => (
        <Link
          key={l.to}
          to={l.to}
          role="menuitem"
          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none"
        >
          <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          {l.label}
        </Link>
      ))}
    </NavDropdown>
  );
}

function NavItem({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `text-sm font-medium transition-colors ${
          isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function MobileLink({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Link
      to={to}
      className="rounded-md px-3 py-3 text-base font-medium hover:bg-muted/50"
    >
      {children}
    </Link>
  );
}

function FooterCol({
  title,
  links,
}: {
  title: string;
  links: Array<{ label: string; href: string }>;
}): JSX.Element {
  return (
    <div>
      <h2 className="text-sm font-semibold">{title}</h2>
      <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
        {links.map((l) => (
          <li key={l.label}>
            <Link to={l.href} className="hover:text-foreground">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
